import { Check, CircleSlash, CornerDownLeft, Loader2, Sparkles } from 'lucide-react'
import { useState } from 'react'

import type { PurposeFitPreview, PurposeRefinement } from '@/api/services/book-ai-purpose'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

const FIT_STYLE: Record<PurposeFitPreview['fit'], { label: string; className: string }> = {
  good: { label: '合い方 高', className: 'bg-purple-500/15 text-purple-700 dark:text-purple-200' },
  partial: { label: '合い方 中', className: 'bg-amber-500/15 text-amber-700 dark:text-amber-200' },
  weak: { label: '合い方 低', className: 'bg-muted text-muted-foreground' },
}

/** 目的とこの本の合い方と、目的に効く章（埋め込みで測った値） */
export const FitPreview = ({ preview }: { preview: PurposeFitPreview | null }) => {
  if (!preview) return null
  const style = FIT_STYLE[preview.fit]
  return (
    <span className="flex flex-wrap items-center gap-1 text-[11px]">
      <span className={cn('rounded-full px-1.5 py-0.5 font-medium', style.className)}>{style.label}</span>
      {preview.sections.length > 0 && (
        <span className="text-muted-foreground">効く章: {preview.sections.map((s) => s.title).join('・')}</span>
      )}
    </span>
  )
}

type Props = {
  result: PurposeRefinement
  draft: string
  isRefining: boolean
  isSaving: boolean
  /** 案を入力欄に入れる（直してから決められる） */
  onPick: (purpose: string) => void
  /** 案のまま決める */
  onDecide: (purpose: string) => void
  /** 質問に答えて磨き直す */
  onAnswer: (answers: { question: string; answer: string }[]) => void
}

/**
 * AI と磨いた結果: この本で答えられること・答えにくいこと、磨いた目的の案（合い方付き）、状況を確かめる質問
 */
export const PurposeRefiner = ({ result, draft, isRefining, isSaving, onPick, onDecide, onAnswer }: Props) => {
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const answered = result.questions
    .map((q) => ({ question: q.question, answer: (answers[q.question] ?? '').trim() }))
    .filter((a) => a.answer)

  return (
    <div className="space-y-3 rounded-lg border border-purple-500/30 bg-background p-2.5">
      <div className="space-y-1">
        <p className="text-xs font-semibold text-muted-foreground">いまの目的</p>
        <FitPreview preview={result.draft_preview} />
      </div>

      {(result.can_answer.length > 0 || result.hard_to_answer.length > 0) && (
        <div className="space-y-1 text-xs">
          <p className="font-semibold text-muted-foreground">この本でわかること</p>
          {result.can_answer.map((item) => (
            <p key={item} className="flex gap-1.5">
              <Check className="mt-0.5 size-3.5 shrink-0 text-purple-500" />
              <span className="wrap-break-word">{item}</span>
            </p>
          ))}
          {result.hard_to_answer.map((item) => (
            <p key={item} className="flex gap-1.5 text-muted-foreground">
              <CircleSlash className="mt-0.5 size-3.5 shrink-0" />
              <span className="wrap-break-word">この本では答えにくい: {item}</span>
            </p>
          ))}
        </div>
      )}

      {result.proposals.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-muted-foreground">磨いた目的の案</p>
          {result.proposals.map((proposal) => {
            const isCurrent = proposal.purpose === draft
            return (
              <div
                key={proposal.purpose}
                className={cn('space-y-1.5 rounded-lg bg-muted/60 p-2.5', isCurrent && 'ring-1 ring-purple-500/60')}
              >
                <p className="text-sm font-medium wrap-break-word">{proposal.purpose}</p>
                <p className="text-xs text-muted-foreground wrap-break-word">{proposal.reason}</p>
                <FitPreview preview={proposal.preview} />
                <div className="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2! text-xs"
                    disabled={isCurrent}
                    onClick={() => onPick(proposal.purpose)}
                  >
                    <CornerDownLeft className="size-3.5" />
                    入力欄に入れて直す
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 px-2! text-xs"
                    disabled={isSaving}
                    onClick={() => onDecide(proposal.purpose)}
                  >
                    <Check className="size-3.5" />
                    これに決める
                  </Button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {result.questions.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground">答えると、もっと絞れます</p>
          {result.questions.map((q) => (
            <div key={q.question} className="space-y-1.5">
              <p className="text-sm wrap-break-word">{q.question}</p>
              <div className="flex flex-wrap gap-1.5">
                {q.options.map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={answers[q.question] === option}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs',
                      answers[q.question] === option
                        ? 'border-purple-500 bg-purple-500/15 text-foreground'
                        : 'border-border text-muted-foreground hover:bg-muted',
                    )}
                    onClick={() => setAnswers((prev) => ({ ...prev, [q.question]: option }))}
                  >
                    {option}
                  </button>
                ))}
              </div>
              <Input
                value={q.options.includes(answers[q.question] ?? '') ? '' : (answers[q.question] ?? '')}
                onChange={(e) => setAnswers((prev) => ({ ...prev, [q.question]: e.target.value }))}
                placeholder="自分の言葉で答える"
                maxLength={300}
                aria-label={`${q.question}への答え`}
                className="h-8 text-xs"
              />
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            disabled={answered.length === 0 || isRefining}
            onClick={() => onAnswer(answered)}
          >
            {isRefining ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
            答えて磨き直す
          </Button>
        </div>
      )}
    </div>
  )
}
